import { PointerEvent as ReactPointerEvent, ReactNode, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@/components/ui/IconButton';
import { useScrollOverflow } from '@/hooks/useScrollOverflow';

const DRAG_THRESHOLD_PX = 8;

interface HorizontalScrollerProps {
    children: ReactNode;
    /** Accessible name for the scroll region, e.g. "Collections". */
    label: string;
    /** Classes for the outer positioning wrapper — breakpoint visibility, margins. */
    className?: string;
    /** Classes appended to the scrolling track — gap, padding overrides. */
    trackClassName?: string;
}

/**
 * Horizontally scrolling strip with chevron affordances for hidden content.
 *
 * Each chevron appears only while content is hidden in that direction, and any
 * overflowing edge gets a soft fade, so the strip reads as scrollable without
 * being dragged. Native touch and trackpad scrolling keeps working; the chevrons
 * are an addition, not a replacement.
 *
 * Visibility classes belong on `className`: they land on the wrapper so the
 * chevrons hide together with the track.
 *
 * @example
 * <HorizontalScroller label="Collections" className="hidden lg:block">
 *     {items.map((item) => <Chip key={item.id} {...item} />)}
 * </HorizontalScroller>
 */
export function HorizontalScroller({
    children,
    label,
    className = '',
    trackClassName = '',
}: HorizontalScrollerProps) {
    const { scrollRef, canScrollStart, canScrollEnd, scrollByPage } = useScrollOverflow();

    const startButtonRef = useRef<HTMLButtonElement>(null);
    const endButtonRef = useRef<HTMLButtonElement>(null);
    const touchGestureRef = useRef<{
        startX: number;
        startScrollLeft: number;
        dragging: boolean;
    } | null>(null);
    const suppressClickRef = useRef(false);

    // Which chevron was last activated, so its focus can be handed over if
    // reaching the edge unmounts it.
    const pendingHandoffRef = useRef<'start' | 'end' | null>(null);

    useEffect(() => {
        const activated = pendingHandoffRef.current;

        if (activated === null) {
            return;
        }

        // That direction is still scrollable, so the button kept both its place
        // in the DOM and its focus.
        if (activated === 'start' ? canScrollStart : canScrollEnd) {
            return;
        }

        pendingHandoffRef.current = null;

        // Reclaim only the focus the unmount actually stole. Anything else means
        // focus legitimately sits elsewhere and must not be moved.
        const active = document.activeElement;
        if (active !== null && active !== document.body) {
            return;
        }

        // The opposite chevron is the control the user would reach for next.
        const handoffTarget = activated === 'start' ? endButtonRef.current : startButtonRef.current;
        handoffTarget?.focus();
    }, [canScrollStart, canScrollEnd]);

    const fadeClass =
        canScrollStart && canScrollEnd
            ? 'scroll-fade-both'
            : canScrollStart
              ? 'scroll-fade-start'
              : canScrollEnd
                ? 'scroll-fade-end'
                : '';

    const chevronHandlers = (direction: 'start' | 'end') => {
        const activate = () => {
            pendingHandoffRef.current = direction;
            scrollByPage(direction === 'start' ? -1 : 1);
        };

        return {
            onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => {
                suppressClickRef.current = false;
                if (event.pointerType !== 'touch') {
                    return;
                }

                event.currentTarget.setPointerCapture(event.pointerId);
                touchGestureRef.current = {
                    startX: event.clientX,
                    startScrollLeft: scrollRef.current?.scrollLeft ?? 0,
                    dragging: false,
                };
            },
            onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => {
                const gesture = touchGestureRef.current;
                if (!gesture) {
                    return;
                }

                const dx = event.clientX - gesture.startX;
                if (!gesture.dragging && Math.abs(dx) > DRAG_THRESHOLD_PX) {
                    gesture.dragging = true;
                    pendingHandoffRef.current = null;
                }

                if (gesture.dragging && scrollRef.current) {
                    scrollRef.current.scrollLeft = gesture.startScrollLeft - dx;
                }
            },
            onPointerUp: () => {
                const gesture = touchGestureRef.current;
                if (!gesture) {
                    return;
                }

                if (!gesture.dragging) {
                    activate();
                }
                suppressClickRef.current = true;
                touchGestureRef.current = null;
            },
            onPointerCancel: () => {
                touchGestureRef.current = null;
            },
            onKeyDown: () => {
                suppressClickRef.current = false;
            },
            onClick: () => {
                if (suppressClickRef.current) {
                    suppressClickRef.current = false;
                    return;
                }

                activate();
            },
        };
    };

    return (
        <div className={`relative ${className}`}>
            <div
                ref={scrollRef}
                role="group"
                aria-label={label}
                className={`flex items-center gap-2 overflow-x-auto overscroll-x-contain no-scrollbar py-1 ${fadeClass} ${trackClassName}`}
            >
                {children}
            </div>
            {canScrollStart && (
                <IconButton
                    ref={startButtonRef}
                    type="button"
                    variant="surface"
                    size="sm"
                    {...chevronHandlers('start')}
                    aria-label={`Scroll ${label} left`}
                    className="absolute left-0 top-1/2 z-10 -translate-y-1/2 touch-pan-y"
                >
                    <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                </IconButton>
            )}
            {canScrollEnd && (
                <IconButton
                    ref={endButtonRef}
                    type="button"
                    variant="surface"
                    size="sm"
                    {...chevronHandlers('end')}
                    aria-label={`Scroll ${label} right`}
                    className="absolute right-0 top-1/2 z-10 -translate-y-1/2 touch-pan-y"
                >
                    <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                </IconButton>
            )}
        </div>
    );
}
